import axios from "axios";
import { baseUrl } from "../utils/envAccess";
import { CREDIT_TYPE_CODE_TO_KEY } from "../utils/providerCategories";

/**
 * Credits are segregated into one bucket per provider category (clinician /
 * fertility doula / nutritionist). Backed by Patient/GetCreditBalance, which
 * returns one row per backend CreditType group - see CREDIT_TYPE_CODE_TO_KEY
 * in providerCategories.js.
 */

/** Shape every consumer relies on. Keys match PROVIDER_CATEGORIES[n].creditKey. */
export const EMPTY_CREDITS = {
  clinician: { remaining: 0 },
  fertilityDoula: { remaining: 0 },
  nutritionist: { remaining: 0 },
};

/**
 * @param {string} token  the logged-in patient's JWT
 * @returns {Promise<{clinician: {remaining: number}, fertilityDoula: {remaining: number}, nutritionist: {remaining: number}}>}
 */
export async function fetchPatientCredits(token) {
  const res = await axios.get(`${baseUrl}Patient/GetCreditBalance`, {
    headers: {
      accept: "text/plain",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  // { Success, Message, Data: [{ CreditTypeId, Code, Name, Balance }, ...] }
  const rows = res?.data?.Data ?? res?.data?.data ?? [];

  const balanceByKey = { clinician: 0, fertilityDoula: 0, nutritionist: 0 };

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const code = row?.Code ?? row?.code;
    const key = CREDIT_TYPE_CODE_TO_KEY[code];
    if (!key) {
      // Every CreditType code is expected to be in the map (see
      // ResolveCreditTypeIdForRoleAsync backend-side) - log rather than
      // silently drop a balance if a new one shows up unmapped.
      console.warn(`fetchPatientCredits: unmapped CreditType code "${code}" - balance ignored.`);
      return;
    }
    balanceByKey[key] += Number(row?.Balance ?? row?.balance ?? 0);
  });

  const result = {};
  Object.keys(balanceByKey).forEach((key) => {
    result[key] = { remaining: Math.max(balanceByKey[key], 0) };
  });

  return result;
}
